import type { CanvasObject } from "./canvas-object";
import { clampFrameToCanvas } from "./canvas-bounds";
import { connectorStoredFrame } from "./connector-geometry";

// コネクタ（接続矢印）の参照整合ロジック。クライアント（dispatcher / 貼り付け）と
// サーバ（WebSocket op-applier / REST ハンドラ）の双方から使い、整合ルールを一元化する。

/**
 * コネクタの端点になれる種別かどうか。ペンストローク・コネクタ自身・コメントスレッド（ピン）・
 * セクション（領域コンテナ。図形ではないため接続対象にしない）は端点にできない。
 * undefined（存在しない id）も不可。
 */
export function isConnectorEndpointObject(object: CanvasObject | undefined): object is CanvasObject {
  return (
    object !== undefined &&
    object.type !== "pen-stroke" &&
    object.type !== "connector" &&
    object.type !== "comment-thread" &&
    object.type !== "section"
  );
}

export type ConnectorNormalizeResult =
  | { readonly ok: true; readonly object: CanvasObject }
  | { readonly ok: false; readonly reason: string };

/**
 * 作成時にコネクタの参照整合を検証し、保存 bbox を端点から再計算（キャンバス内へ clamp）した
 * オブジェクトを返す。コネクタ以外はそのまま ok で返す。
 *
 * - 始点と終点が同一オブジェクトなら拒否（自己ループ禁止）。
 * - いずれかの端点が存在しない／端点にできない種別（ペン・コネクタ）なら拒否。
 * - 妥当なら、端点の現在位置から保存 bbox を再計算して上書きする（クライアントが任意の
 *   x/y/w/h を詰めて送っても、端点ルートと無関係な保存値を残さない）。
 *
 * objectsById には「作成後に存在するはずの全オブジェクト」を渡す
 * （サーバ DB の既存分 + 同一バッチ内の新規分など）。
 */
export function normalizeConnectorForCreate(
  object: CanvasObject,
  objectsById: ReadonlyMap<string, CanvasObject>
): ConnectorNormalizeResult {
  if (object.type !== "connector") return { ok: true, object };
  const { from, to } = object;
  if (from.objectId === to.objectId) {
    return { ok: false, reason: "コネクタの始点と終点が同一オブジェクトです。" };
  }
  const fromObj = objectsById.get(from.objectId);
  const toObj = objectsById.get(to.objectId);
  if (!isConnectorEndpointObject(fromObj) || !isConnectorEndpointObject(toObj)) {
    return {
      ok: false,
      reason: "コネクタの端点が存在しないか、接続できない種別（ペン / コネクタ）です。",
    };
  }
  const frame = clampFrameToCanvas(
    connectorStoredFrame(
      { x: fromObj.x, y: fromObj.y, w: fromObj.w, h: fromObj.h, rotation: fromObj.rotation ?? 0 },
      from.side,
      { x: toObj.x, y: toObj.y, w: toObj.w, h: toObj.h, rotation: toObj.rotation ?? 0 },
      to.side
    )
  );
  return {
    ok: true,
    object: { ...object, x: frame.x, y: frame.y, w: frame.w, h: frame.h },
  };
}

/**
 * 指定 id 集合のいずれかを端点（from / to）に持つコネクタの id を返す。
 * 図形削除時に、その図形へ接続しているコネクタも巻き込んで削除するために使う。
 * 既に削除対象へ含まれているコネクタ自身は二重計上しない。
 */
export function collectConnectorCascadeDeleteIds(
  objects: Iterable<CanvasObject>,
  deletedIds: ReadonlySet<string>
): string[] {
  const result: string[] = [];
  for (const object of objects) {
    if (object.type !== "connector") continue;
    if (deletedIds.has(object.id)) continue;
    if (deletedIds.has(object.from.objectId) || deletedIds.has(object.to.objectId)) {
      result.push(object.id);
    }
  }
  return result;
}
